import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { cn } from '@/lib/utils';
import type { ShiftWithTotals } from '@/features/shifts/schemas';
import type { ServiceDTO } from '@/features/services/api';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

interface CalendarGridProps {
  month: string; // YYYY-MM
  shifts: ShiftWithTotals[];
  services: ServiceDTO[];
  onDayClick: (dateISO: string) => void;
}

export function CalendarGrid({ month, shifts, services, onDayClick }: CalendarGridProps) {
  const monthStart = parseISO(`${month}-01`);
  const gridStart = startOfWeek(startOfMonth(monthStart), { weekStartsOn: 0 });
  const gridEnd = endOfWeek(endOfMonth(monthStart), { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const serviceMap = new Map(services.map((s) => [s.id, s]));
  const byDate = new Map<string, ShiftWithTotals[]>();
  for (const s of shifts) {
    const list = byDate.get(s.shift.date) ?? [];
    list.push(s);
    byDate.set(s.shift.date, list);
  }

  return (
    <div className="select-none">
      <div className="grid grid-cols-7 gap-1 pb-2 text-center text-xs font-medium text-(--color-fg-muted)">
        {WEEKDAYS.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const iso = format(day, 'yyyy-MM-dd');
          const inMonth = isSameMonth(day, monthStart);
          const dayShifts = byDate.get(iso) ?? [];
          return (
            <button
              type="button"
              key={iso}
              onClick={() => onDayClick(iso)}
              aria-label={`Dia ${format(day, 'dd/MM/yyyy')}, ${dayShifts.length} plantão(ões)`}
              className={cn(
                'flex min-h-24 flex-col items-stretch rounded-md border border-(--color-border) bg-(--color-surface) p-1.5 text-left transition-colors hover:bg-(--color-surface-elevated) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)',
                !inMonth && 'opacity-40',
                isToday(day) && 'border-(--color-accent)',
              )}
            >
              <span
                className={cn(
                  'text-xs font-semibold',
                  isToday(day) && 'text-(--color-accent)',
                )}
              >
                {format(day, 'd')}
              </span>
              <div className="mt-1 flex flex-col gap-1 overflow-hidden">
                {dayShifts.slice(0, 3).map((s) => {
                  const svc = serviceMap.get(s.shift.serviceId);
                  return (
                    <span
                      key={s.shift.id}
                      className={cn(
                        'truncate rounded-sm px-1 py-0.5 text-[10px] font-medium',
                        statusClasses(s.shift.status),
                      )}
                      style={{
                        borderLeft: `3px solid ${svc?.color ?? 'currentColor'}`,
                      }}
                    >
                      {svc?.name ?? '—'}
                    </span>
                  );
                })}
                {dayShifts.length > 3 && (
                  <span className="text-[10px] text-(--color-fg-muted)">
                    +{dayShifts.length - 3}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function statusClasses(status: 'PENDING' | 'ACTIVE' | 'DONE'): string {
  switch (status) {
    case 'PENDING':
      return 'bg-(--color-warning)/20 text-(--color-warning)';
    case 'ACTIVE':
      return 'bg-(--color-accent)/20 text-(--color-accent)';
    case 'DONE':
      return 'bg-(--color-success)/20 text-(--color-success)';
  }
}
